import { useEffect } from "react"
import { Head, usePage } from "@inertiajs/react"
import { toast } from "sonner"

import { StaffLayout } from "@/components/layouts/staff-layout"
import { MainContainer } from "@/components/ui/container"
import { PageHeader } from "@/components/ui/headers"
import { TillCashMovements } from "@/features/staff/till-cash-movements"
import { TillJourneyStrip } from "@/features/staff/till-journey"
import { TillOpenForm } from "@/features/staff/till-open-form"
import { TillPreviousClose } from "@/features/staff/till-previous-close"
import { TillReportPanel } from "@/features/staff/till-report"
import { TillStockPanel } from "@/features/staff/till-stock-panel"
import { TillSummary } from "@/features/staff/till-summary"
import { cn } from "@/lib/utils"
import {
  tTill,
  type ShiftCloseSummary,
  type ShiftJourney,
  type ShiftReport,
  type TillItemStock,
  type TillOption,
} from "@/features/staff/till"

/**
 * The till.
 *
 * Read by someone standing at a counter, mid-service, with their hands full, so
 * the screen answers the three questions that person asks — in the order they
 * ask them:
 *
 *   ¿cómo quedó la caja?   — the last signed close, four figures and a name.
 *   ¿qué jornada es hoy?   — the hours the restaurant keeps, and where the
 *                            clock is inside them.
 *   ¿con qué arranco?      — the float, and the dish counts the day starts on.
 *
 * It still has exactly three shapes and never a fourth:
 *
 *   no shift ever opened  — the open form, and nothing to report yet.
 *   a shift is open       — the live drawer, its movements, and the close.
 *   the last shift closed — the open form for the next one, and the Z of the
 *                           one just signed, now a tap away rather than pinned
 *                           to the bottom of the screen.
 *
 * Whichever it is, the thing to do about it is already on screen. There is no
 * "open the till" button that reveals a form, and no collapsed section hiding
 * the movement fields.
 *
 * ## Two columns, and which is which
 *
 * Left is what is happening to the money right now — the drawer, its movements,
 * its cut. Right is what the day is made of — the last close, and the dishes.
 * The split is by what the reader is doing, not by size: money is worked, the
 * right column is consulted. On anything narrower than `lg` it stacks in that
 * same order, so the phone reads top to bottom exactly as the desk reads left
 * to right.
 *
 * The shift owns the *period*, not the permission: staff-originated cash and
 * terminal payments need one open, and the diner scanning a QR at a table never
 * does. That asymmetry is why the report on this page is careful about what it
 * claims to cover — see `features/staff/till-report.tsx`.
 *
 * Every write here answers with a redirect and a flash rather than a validation
 * bag (the services return a `Result`, not model errors), so the flash is what
 * the cashier reads. It is surfaced as a toast, matching the rest of staff.
 */

type Flash = {
  notice?: string
  alert?: string
}

type Props = {
  report: ShiftReport | null
  previousClose: ShiftCloseSummary | null
  journey: ShiftJourney
  /** Null where the restaurant does not count dishes; no arqueo, no panel. */
  itemStock: TillItemStock | null
  timeZone: string
  movementKinds: TillOption[]
  movementReasons: TillOption[]
  flash?: Flash
}

/**
 * One toast per server response that carried a flash.
 *
 * Keyed on the `flash` object rather than on its text. Inertia hands back a
 * fresh props object per response, so referential identity is exactly "a new
 * answer arrived" — while comparing the string swallows the second identical
 * one. That is not hypothetical here: the close dialog submits with
 * `preserveState`, so the page never remounts, and a cashier who confirms a
 * close twice with the same two bills still open would be refused twice and
 * shown nothing the second time. A button that appears to do nothing is worse
 * than the error it is hiding.
 */
function useFlashToasts(flash: Flash) {
  useEffect(() => {
    if (flash.notice) toast.success(flash.notice)
    if (flash.alert) toast.error(flash.alert)
  }, [flash])
}

export default function StaffLocationShiftsShow() {
  const {
    report,
    previousClose = null,
    journey,
    itemStock = null,
    timeZone,
    movementKinds = [],
    movementReasons = [],
    flash = {},
  } = usePage<Props>().props

  useFlashToasts(flash)

  const openShift = report && report.shift.status === "open" ? report : null
  // The full Z is in memory only when the report on this page IS the shift the
  // summary describes, which is exactly when no drawer is open. See
  // `TillPreviousClose`: without it, the card offers no link at all.
  const previousReport =
    previousClose && report && report.shift.id === previousClose.id ? report : null

  // The right column exists only when it has something in it. A restaurant on
  // its first shift, with no dish counting, would otherwise get 340px of empty
  // canvas holding the drawer off-centre.
  const asideStock = openShift && itemStock ? itemStock : null
  const hasAside = Boolean(asideStock || previousClose)

  return (
    <>
      <Head title={tTill("title")} />

      <MainContainer>
        <div className="space-y-4" data-testid="shift-screen">
          <PageHeader title={tTill("title")} description={tTill("description")} />

          <TillJourneyStrip journey={journey} />

          <div
            className={cn(
              "grid gap-4 lg:items-start",
              hasAside && "lg:grid-cols-[minmax(0,1fr)_340px]"
            )}
          >
            <div className="space-y-4">
              {openShift ? (
                <>
                  <TillSummary report={openShift} timeZone={timeZone} />
                  <TillCashMovements
                    movements={openShift.movements}
                    kindOptions={movementKinds}
                    reasonOptions={movementReasons}
                    timeZone={timeZone}
                  />
                  <TillReportPanel report={openShift} timeZone={timeZone} />
                </>
              ) : (
                <TillOpenForm hadPreviousShift={report !== null} itemStock={itemStock} />
              )}
            </div>

            {hasAside ? (
              <div className="space-y-4">
                {asideStock && openShift ? (
                  <TillStockPanel
                    itemStock={asideStock}
                    shift={openShift.shift}
                    timeZone={timeZone}
                  />
                ) : null}

                {previousClose ? (
                  <TillPreviousClose
                    summary={previousClose}
                    timeZone={timeZone}
                    report={previousReport}
                  />
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      </MainContainer>
    </>
  )
}

StaffLocationShiftsShow.layout = (page: React.ReactNode) => <StaffLayout>{page}</StaffLayout>
